// =============================================================================
// domain/patternCategories.ts — the four columns of the Setups kanban.
// -----------------------------------------------------------------------------
// A category is `{ name, color }`, stored as an ARRAY in
// `user_preferences.pattern_categories`. The order of that array is the column
// order on screen, so it is data, not a rendering detail.
//
// ── 🚨 `pattern.category` IS THE NAME, A PLAIN STRING — NOT AN ID ──
//
// The old client says why, in its own words: the field is "kept as a string for
// backward compatibility with pre-v2 data", and RENAMING a category migrates
// every pattern's string at save time. So a pattern whose `category` does not
// match any current name is not pointing at a missing row — it is holding a name
// that no longer exists, which is a different thing and shows up differently:
// `patternCategoryOf` returns `''` and the pattern lands in the uncategorized
// banner rather than vanishing.
//
// ⚠ That is also why there is no id-based lookup here, and why adding one later
// would be a schema change rather than a refactor.
// =============================================================================

export interface PatternCategory {
  name: string;
  color: string;
}

/**
 * The seed list, transcribed from the old client's `DEFAULT_PATTERN_CATEGORIES`.
 *
 * ⚠ These are NOT Josh's categories. His preferences hold four different ones
 * (`PDC - TRENDING`, `FADES - BALANCED`, `REVERSALS`, `OTHER`), because the old
 * client seeds this list once and every edit after that lives in settings. So a
 * screen showing these four names is showing a user whose preferences did not
 * load — which is worth being able to recognise.
 */
export const DEFAULT_PATTERN_CATEGORIES: readonly PatternCategory[] = [
  { name: 'PDC', color: '#7cc4ff' },
  { name: 'FADES', color: '#f5b342' },
  { name: 'GEX/DEX', color: '#c89bff' },
  { name: 'ORDER FLOW', color: '#5fd39a' },
];

/**
 * Parse the stored array.
 *
 * ⚠ An EMPTY array falls back to the defaults, matching `getPatternCategories()`
 * — "(Array.isArray(cats) && cats.length > 0) ? cats : DEFAULT". Not a defensive
 * flourish: with no categories there are no columns, so every pattern would fall
 * into the uncategorized banner and the kanban would render as an empty grid over
 * a list of eleven chips.
 *
 * ⚠ Entries without a usable `name` are DROPPED rather than rendered as a blank
 * column, because `patternCategoryOf` matches on the name and a pattern can never
 * hold `''` as a real category — that value already means uncategorized.
 */
export function patternCategories(stored: unknown): readonly PatternCategory[] {
  if (!Array.isArray(stored) || stored.length === 0) return DEFAULT_PATTERN_CATEGORIES;
  const out: PatternCategory[] = [];
  for (const c of stored as unknown[]) {
    if (!c || typeof c !== 'object') continue;
    const r = c as Record<string, unknown>;
    const name = typeof r['name'] === 'string' ? r['name'].trim() : '';
    if (!name) continue;
    out.push({
      name,
      // ⚠ The old client's own fallback, verbatim — a CSS variable reference, not
      // a hex. A category saved without a colour still gets a legible heading
      // rather than an invalid `color: ""` that inherits whatever is above it.
      color: typeof r['color'] === 'string' && r['color'].trim()
        ? r['color'].trim()
        : 'var(--text-2)',
    });
  }
  return out.length ? out : DEFAULT_PATTERN_CATEGORIES;
}

export function patternCategoryNames(cats: readonly PatternCategory[]): string[] {
  return cats.map((c) => c.name);
}

export function patternCategoryColor(
  cats: readonly PatternCategory[], name: string,
): string {
  return cats.find((c) => c.name === name)?.color ?? 'var(--text-2)';
}

/**
 * A pattern's category, normalised to one of the known names — or `''`.
 *
 * ⚠ TRIMS BEFORE MATCHING, as the old client does. `" REVERSALS"` with a stray
 * leading space is a real shape in hand-edited data, and an untrimmed compare
 * would drop that pattern into the uncategorized banner while its stored value
 * looks correct in every other view.
 */
export function patternCategoryOf(
  cats: readonly PatternCategory[], category: string | null | undefined,
): string {
  const c = typeof category === 'string' ? category.trim() : '';
  return c && cats.some((x) => x.name === c) ? c : '';
}
